import { Link } from "react-router-dom";
import { Phone, ArrowRight, Search, SprayCan, Wind, Flame, Droplets, Building2, BadgeCheck, CreditCard, CalendarCheck, Hotel } from "lucide-react";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import Seo from "@/components/Seo";
import CtaSection from "@/components/CtaSection";
import EstimateurSection from "@/components/EstimateurSection";
import GuaranteeBadge from "@/components/GuaranteeBadge";
import { CALENDLY_URL } from "@/data/site";

const PriceTable = ({ columns, rows, testid }) => (
    <>
        {/* Desktop : tableau */}
        <div data-testid={testid} className="hidden overflow-x-auto rounded-3xl border border-black/10 bg-white shadow-sm md:block">
            <table className="w-full text-left text-sm">
                <thead>
                    <tr className="bg-brand text-white">
                        {columns.map((c) => (
                            <th key={c} className="px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.2em]">{c}</th>
                        ))}
                        <th className="px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.2em]">Sam., dim. &amp; fériés (+20 %)</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className={`border-t border-black/5 ${i % 2 ? "bg-mist/60" : "bg-white"}`}>
                            <td className="px-6 py-4 font-semibold text-night">{r[0]}</td>
                            <td className="px-6 py-4 text-slate-600">{r[1]}</td>
                            <td className="px-6 py-4 font-display text-lg font-black text-brand">{r[2]}</td>
                            <td className="px-6 py-4 font-display text-lg font-black text-amber-600">{dimanchePrice(r[2])}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        {/* Mobile : cartes empilées, aucun glissement nécessaire */}
        <div data-testid={`${testid}-mobile`} className="space-y-3 md:hidden">
            {rows.map((r, i) => (
                <div key={i} className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="font-semibold text-night">{r[0]}</p>
                            <p className="mt-0.5 text-sm text-slate-500">{r[1]}</p>
                        </div>
                        <p className="shrink-0 font-display text-xl font-black text-brand">{r[2]}</p>
                    </div>
                    {dimanchePrice(r[2]) !== r[2] && (
                        <p className="mt-2 inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
                            Sam., dim. &amp; fériés : {dimanchePrice(r[2])}
                        </p>
                    )}
                </div>
            ))}
        </div>
    </>
);

const dimanchePrice = (val) =>
    val.replace(/(\d+)(?:[,.](\d+))?\s*€/g, (full, intPart, decPart) => {
        const num = parseFloat(intPart + (decPart ? "." + decPart : "0"));
        if (Number.isNaN(num)) return full;
        const maj = Math.round(num * 1.2 * 100) / 100;
        const f = Number.isInteger(maj) ? `${maj},00` : maj.toFixed(2).replace(".", ",");
        return `${f} €`;
    });

const SECTIONS = [
    {
        num: "01",
        icon: Search,
        title: "Recherche de punaises de lit",
        intro: "Forfait unique de recherche à votre domicile. Si nous détectons une infestation et que le traitement est réalisé immédiatement, le jour même, la recherche est déduite de la facture finale.",
        columns: ["Logement", "Prestation", "Prix HT"],
        rows: [
            ["Appartement / Maison", "Recherche de punaises de lit dans un logement", "59,00 €"],
            ["Hôtel", "Recherche de punaises de lit dans une chambre", "49 à 59,00 €"],
        ],
        note: "En cas d'infestation confirmée, la recherche est déduite du traitement réalisé le jour même. Toute intervention reportée à un autre jour ne donne pas lieu à déduction.",
        testid: "tarifs-table-recherche",
    },
    {
        num: "02",
        icon: SprayCan,
        title: "Punaises de lit, traitement chimique",
        intro: "Face à la résistance des punaises de lit aux traitements conventionnels, nos insecticides professionnels rémanents garantissent une éradication durable. Tarif selon la superficie du logement.",
        columns: ["Superficie", "Type de traitement", "Prix HT"],
        rows: [
            ["0 à 30 m²", "Pulvérisation", "180,00 €"],
            ["31 à 60 m²", "Pulvérisation", "190,00 €"],
            ["61 à 90 m²", "Pulvérisation", "210,00 €"],
            ["91 à 120 m²", "Pulvérisation", "230,00 €"],
            ["121 à 150 m²", "Pulvérisation", "250,00 €"],
            ["151 à 180 m²", "Pulvérisation", "270,00 €"],
            ["181 m² et plus", "Pulvérisation", "Sur devis"],
        ],
        testid: "tarifs-table-chimique-punaises",
    },
    {
        num: "03",
        icon: Wind,
        title: "Punaises de lit, vapeur sèche 180°C",
        intro: "Le jet de vapeur sèche, diffusé par un pistolet, est une méthode ciblée et précise, idéale pour les meubles, vêtements, sommiers et zones infestées. La chaleur détruit instantanément punaises, larves et œufs.",
        columns: ["Superficie", "Type de traitement", "Prix HT"],
        rows: [
            ["0 à 30 m²", "Propulsion vapeur 180°", "350,00 €"],
            ["31 à 60 m²", "Propulsion vapeur 180°", "400,00 €"],
            ["61 à 90 m²", "Propulsion vapeur 180°", "450,00 €"],
            ["91 à 120 m²", "Propulsion vapeur 180°", "500,00 €"],
            ["121 à 150 m²", "Propulsion vapeur 180°", "550,00 €"],
            ["151 à 180 m²", "Propulsion vapeur 180°", "600,00 €"],
            ["181 m² et plus", "Propulsion vapeur 180°", "Sur devis"],
        ],
        extras: [
            ["Vapeur sèche pour 1 canapé", "90,00 €"],
            ["Vapeur sèche pour 1 lit", "100,00 €"],
        ],
        testid: "tarifs-table-vapeur",
    },
    {
        num: "04",
        icon: Flame,
        title: "Fumigène combustible effet choc",
        intro: "Un combustible à effet prolongé et résiduel qui neutralise efficacement les punaises de lit sur une durée étendue, y compris celles devenues résistantes aux insecticides traditionnels.",
        columns: ["Superficie", "Type de traitement", "Prix HT"],
        rows: [
            ["À partir de 10 m²", "Fumigène combustible effet choc longue rémanence", "160,00 €"],
        ],
        testid: "tarifs-table-fumigene",
    },
    {
        num: "05",
        icon: Droplets,
        title: "Cafards & blattes, gel professionnel",
        intro: "Un gel professionnel à l'efficacité directe et durable pour une élimination rapide. Des contrats annuels sont également disponibles pour une protection permanente, particuliers comme professionnels.",
        columns: ["Clientèle", "Type de traitement", "Prix HT"],
        rows: [
            ["Particulier", "Gel anti-blattes et cafards (cuisine, salle d'eau, WC)", "59,00 €"],
            ["Professionnel", "Gel anti-blattes et cafards (hôtel, restaurant...)", "Sur devis"],
            ["Association", "Gel anti-blattes et cafards (cuisine, salle d'eau, WC)", "Sur devis"],
        ],
        testid: "tarifs-table-gel-cafards",
    },
    {
        num: "06",
        icon: Building2,
        title: "Cafards & blattes, traitement chimique",
        intro: "Grâce à nos techniques de pulvérisation chimique avancées et des produits sûrs et éprouvés, nous assurons une éradication rapide et efficace des infestations de cafards.",
        columns: ["Superficie", "Type de traitement", "Prix HT"],
        rows: [
            ["0 à 30 m²", "Pulvérisation", "170,00 €"],
            ["31 à 60 m²", "Pulvérisation", "190,00 €"],
            ["61 à 90 m²", "Pulvérisation", "210,00 €"],
            ["91 à 120 m²", "Pulvérisation", "230,00 €"],
            ["121 à 150 m²", "Pulvérisation", "250,00 €"],
            ["151 à 180 m²", "Pulvérisation", "Sur devis"],
        ],
        testid: "tarifs-table-chimique-cafards",
    },
    {
        num: "07",
        icon: Hotel,
        title: "Hôtels & hébergements touristiques",
        intro: "Tarif réservé aux professionnels de l'hébergement. Intervention en toute discrétion avec un véhicule blanc banalisé, de 8h à 22h, 7j/7 : vos clients ne remarquent rien. Traitement vapeur sèche 180°C sans produit chimique : la chambre est relouable le jour même, aucune nuitée perdue.",
        columns: ["Prestation", "Détails", "Prix HT"],
        rows: [
            ["Chambre d'hôtel", "Traitement vapeur sèche 180°C, chambre relouable le jour même", "Dès 190,00 €"],
            ["Recherche / diagnostic", "Détection de punaises de lit dans une chambre", "49 à 59,00 €"],
            ["Plusieurs chambres ou étage", "Traitement groupé, contrat annuel de protection", "Sur devis"],
        ],
        note: "Recherche déduite si le traitement est réalisé le jour même. Véhicule banalisé et rapport écrit fourni pour votre établissement.",
        testid: "tarifs-table-hotels",
    },
];

const Tarifs = () => {
    return (
        <div data-testid="tarifs-page">
            <Seo
                title="Tarifs 2026 punaises de lit & cafards | Grille complète | Eradikbreizh"
                description="Grille tarifaire 2026 : recherche 59 €, traitement chimique dès 180 €, vapeur 180°C dès 350 €, fumigène 160 €, gel cafards 59 €. Majoration de 20 % les samedis, dimanches et jours fériés."
            />
            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-16 pt-40 md:pt-48">
                <div className="absolute -right-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <MaskedLine delay={0.1}><SectionTag>Grille complète, du lundi au dimanche</SectionTag></MaskedLine>
                    <h1 className="mt-6 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl">
                        <MaskedLine delay={0.25}>Tarifs</MaskedLine>
                        <MaskedLine delay={0.4}>
                            <span className="text-brand">2026</span>
                        </MaskedLine>
                    </h1>
                    <MaskedLine delay={0.55}>
                        <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                            Tous nos prix HT, TVA non applicable (art. 293 B du CGI). Une majoration de 20 % s'applique les samedis, dimanches et jours fériés.
                            Le prix affiché est le prix facturé. Intervention possible sous 2h, 7j/7.
                        </p>
                    </MaskedLine>
                    <MaskedLine delay={0.7}>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-slate-600 shadow-sm">
                                <BadgeCheck className="h-4 w-4 text-brand" />
                                Agréé CertiBIOCIDE
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-slate-600 shadow-sm">
                                <Phone className="h-4 w-4 text-brand" />
                                02 55 99 22 43
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-slate-600 shadow-sm">
                                <CreditCard className="h-4 w-4 text-brand" />
                                Paiement CB sur place
                            </span>
                            <GuaranteeBadge neutral testid="tarifs-guarantee-badge" />
                        </div>
                    </MaskedLine>
                    <MaskedLine delay={0.8}>
                        <p className="mt-5 flex items-center gap-2 text-sm font-semibold text-slate-600">
                            <CreditCard className="h-4 w-4 shrink-0 text-brand" />
                            Paiement par CB uniquement, sur place. Vous n'êtes pas sur place ? Le paiement s'effectue intégralement avant la prestation.
                        </p>
                    </MaskedLine>
                </div>
            </section>

            {/* SECTIONS */}
            {SECTIONS.map((section, i) => (
                <section
                    key={section.num}
                    className={`py-16 md:py-20 ${i % 2 ? "bg-mist" : "bg-white"}`}
                >
                    <div className="mx-auto max-w-5xl px-5 sm:px-8">
                        <Reveal>
                            <div className="flex items-start gap-5">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                    <section.icon className="h-7 w-7" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand">Tarif {section.num}</p>
                                    <h2 className="mt-2 font-display text-3xl font-black uppercase leading-tight tracking-tight sm:text-4xl">
                                        {section.title}
                                    </h2>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">{section.intro}</p>
                        </Reveal>
                        {section.extras && (
                            <Reveal delay={0.15} className="mt-8 grid gap-4 sm:grid-cols-2">
                                {section.extras.map(([label, price]) => (
                                    <div key={label} className="flex items-center justify-between rounded-2xl border border-brand/30 bg-brand/5 px-6 py-4">
                                        <div>
                                            <span className="text-sm font-semibold text-night">{label}</span>
                                            {dimanchePrice(price) !== price && (
                                                <span className="mt-0.5 block text-xs font-semibold text-amber-600">Sam., dim. &amp; fériés : {dimanchePrice(price)}</span>
                                            )}
                                        </div>
                                        <span className="font-display text-2xl font-black text-brand">{price}</span>
                                    </div>
                                ))}
                            </Reveal>
                        )}
                        <Reveal delay={0.2} className="mt-8">
                            <PriceTable columns={section.columns} rows={section.rows} testid={section.testid} />
                        </Reveal>
                        {section.note && (
                            <Reveal delay={0.25}>
                                <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand/10 px-5 py-2.5 text-sm font-semibold text-brand">
                                    <BadgeCheck className="h-4 w-4" />
                                    {section.note}
                                </p>
                            </Reveal>
                        )}
                        <Reveal delay={0.3} className="mt-8">
                            <a
                                data-testid={`${section.testid}-booking-button`}
                                href={CALENDLY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                            >
                                <CalendarCheck className="h-5 w-5" />
                                Réserver ce traitement en ligne en 2 min
                            </a>
                        </Reveal>
                    </div>
                </section>
            ))}

            <EstimateurSection dark />

            {/* HELP */}
            <section className="bg-white py-16 md:py-20">
                <div className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-8 px-5 sm:px-8 md:flex-row md:items-center">
                    <Reveal>
                        <h2 className="font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">
                            Un cas <span className="text-brand">spécifique</span> ?
                        </h2>
                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">
                            Logement atypique, infestation sévère, bailleur ou syndic : décrivez-nous votre situation via le formulaire, nous vous rappelons avec un devis précis. Sinon, appelez-nous : estimation en 2 minutes au téléphone.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="flex flex-wrap gap-4">
                        <a
                            data-testid="tarifs-phone-button"
                            href="tel:+33255992243"
                            className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                        >
                            <Phone className="h-5 w-5" />
                            02 55 99 22 43
                        </a>
                        <Link
                            data-testid="tarifs-devis-button"
                            to="/contact"
                            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                        >
                            Décrire mon cas
                            <ArrowRight className="h-5 w-5 text-brand" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <CtaSection
                title="Un nuisible chez vous ?"
                subtitle="Tarifs transparents, intervention possible sous 2h, 7j/7 de 9h à 20h partout en Bretagne."
            />
        </div>
    );
};

export default Tarifs;
